import React, { useState, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Calendar,
  Clock,
  CheckCircle,
  Users,
  IndianRupee,
  RefreshCw,
  Phone,
  ArrowRight,
  UserPlus,
  ClipboardList,
  Search,
  User,
  Loader2,
  Eye
} from 'lucide-react';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { toast } from '@/hooks/use-toast';
import { useAuth } from '@/context/AuthContext';
import { getAllAppointments, getPatients, getDoctors, queueCheckin, getQueueToday, queueWalkin, queueWalkinRegister, queueCancel, queueReorder, searchPatients, getBillingServices, createBillingInvoice, type QueueEntry, type QueueTodayResponse } from '@/lib/api';
import WalkinModal from '@/components/FrontDesk/WalkinModal';
import PatientFormModal from '@/components/Patient/PatientFormModal';
import MedicalBillingModal from '@/components/Patient/MedicalBillingModal';

interface Patient {
  id: string;
  name: string;
  age: number;
  gender: string;
  phone: string;
  mrn?: string;
}

interface Appointment {
  id: string;
  patient_id: string;
  date: string;
  time: string;
  reason: string;
  status: 'scheduled' | 'checked_in' | 'in_progress' | 'completed' | 'cancelled';
  visit_type?: string;
}

interface AppointmentWithPatient extends Appointment {
  patientName: string;
  patientPhone: string;
  patientMrn?: string;
}

export default function ReceptionistDashboard() {
  const navigate = useNavigate();
  const { currentUser } = useAuth();
  
  const [appointments, setAppointments] = useState<Appointment[]>([]);
  const [patients, setPatients] = useState<Patient[]>([]);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [checkingInId, setCheckingInId] = useState<string | null>(null);
  const [queueData, setQueueData] = useState<QueueTodayResponse | null>(null);
  const [queueActionId, setQueueActionId] = useState<string | null>(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [searchResults, setSearchResults] = useState<Patient[]>([]);
  const [showWalkinModal, setShowWalkinModal] = useState(false);
  const [doctors, setDoctors] = useState<{id: string, name: string}[]>([]);
  const [selectedDoctorId, setSelectedDoctorId] = useState<string | null>(null);
  const [showPatientFormModal, setShowPatientFormModal] = useState(false);
  const [prefilledPhone, setPrefilledPhone] = useState('');
  const [searchLoading, setSearchLoading] = useState(false);
  const [showBillingModal, setShowBillingModal] = useState(false);
  const [billingPatient, setBillingPatient] = useState<Patient | null>(null);
  const [billingServices, setBillingServices] = useState<any[]>([]);
  const [billingLoading, setBillingLoading] = useState(false);

  const searchDebounceRef = React.useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(() => {
    loadData();
  }, []);

  useEffect(() => {
    const interval = setInterval(() => {
      if (selectedDoctorId && currentUser?.clinic_id) {
        getQueueToday(selectedDoctorId, currentUser.clinic_id)
          .then(data => { if (data) setQueueData(data); })
          .catch(() => {});
      }
    }, 30000);
    return () => clearInterval(interval);
  }, [selectedDoctorId, currentUser]);

  useEffect(() => {
    if (!selectedDoctorId || !currentUser?.clinic_id) return;
    getQueueToday(selectedDoctorId, currentUser.clinic_id)
      .then(data => { if (data) setQueueData(data); })
      .catch(() => {});
  }, [selectedDoctorId]);

  const loadData = async () => {
    try {
      setLoading(true);
      const clinicId = currentUser?.clinic_id;
      const [appointmentsData, patientsData, doctorsData] = await Promise.all([
        getAllAppointments(currentUser?.tenant_id ?? '', currentUser?.clinic_id ?? ''),
        clinicId ? getPatients({ clinic_id: clinicId }) : Promise.resolve({ patients: [], total: 0 }),
        getDoctors()
      ]);
      if (appointmentsData) setAppointments(appointmentsData);
      if (patientsData?.patients) setPatients(patientsData.patients);
      if (doctorsData && doctorsData.length > 0) {
        setDoctors(doctorsData);
        setSelectedDoctorId(prev => prev ?? doctorsData[0].id);
      }
    } catch (err) {
      console.error('Error loading data:', err);
      toast({
        title: "Error",
        description: "Failed to load dashboard data",
        variant: "destructive"
      });
    } finally {
      setLoading(false);
    }
  };

  const handleSearch = (query: string) => {
    setSearchQuery(query);
    if (query.length < 2) {
      setSearchResults([]);
      return;
    }
    if (searchDebounceRef.current) {
      clearTimeout(searchDebounceRef.current);
    }
    searchDebounceRef.current = setTimeout(async () => {
      setSearchLoading(true);
      try {
        const results = await searchPatients(
          query,
          currentUser?.clinic_id
        );
        setSearchResults(
          Array.isArray(results) ? results.slice(0, 5) : []
        );
      } catch {
        setSearchResults([]);
      } finally {
        setSearchLoading(false);
      }
    }, 300);
  };

  const handleRefresh = async () => {
    setRefreshing(true);
    await loadData();
    setRefreshing(false);
    toast({ title: "Refreshed", description: "Data refreshed successfully" });
  };

  const handleCheckIn = async (apt: AppointmentWithPatient) => {
    if (apt.status !== 'scheduled') return;
    try {
      setCheckingInId(apt.id);
      const entry = await queueCheckin(apt.id);
      await loadData();
      toast({
        title: "Checked In",
        description: entry ? `Token #${entry.tokenNumber} — ${apt.patientName}` : `${apt.patientName} checked in`,
      });
    } catch (err: unknown) {
      const msg = err instanceof Error ? err.message : "Check-in failed";
      toast({ title: "Error", description: msg, variant: "destructive" });
    } finally {
      setCheckingInId(null);
    }
  };

  const handlePriority = async (entry: QueueEntry) => {
    if (!selectedDoctorId) {
      toast({ title: "Select a doctor first", description: "Please select a doctor to manage queue", variant: "destructive" });
      return;
    }
    try {
      setQueueActionId(entry.id);
      await queueReorder(entry.id, true);
      const updated = await getQueueToday(selectedDoctorId, currentUser!.clinic_id!);
      if (updated) setQueueData(updated);
      toast({ title: "Moved to priority", description: `${entry.patientName} moved to front` });
    } catch (err: unknown) {
      const msg = err instanceof Error ? err.message : "Failed";
      toast({ title: "Error", description: msg, variant: "destructive" });
    } finally {
      setQueueActionId(null);
    }
  };

  const handleRemoveFromQueue = async (entry: QueueEntry) => {
    if (!selectedDoctorId) {
      toast({ title: "Select a doctor first", description: "Please select a doctor to manage queue", variant: "destructive" });
      return;
    }
    if (!window.confirm(`Remove ${entry.patientName} from queue?`)) return;
    try {
      setQueueActionId(entry.id);
      await queueCancel(entry.id);
      const updated = await getQueueToday(selectedDoctorId, currentUser!.clinic_id!);
      if (updated) setQueueData(updated);
      toast({ title: "Removed", description: `${entry.patientName} removed from queue` });
    } catch (err: unknown) {
      const msg = err instanceof Error ? err.message : "Failed";
      toast({ title: "Error", description: msg, variant: "destructive" });
    } finally {
      setQueueActionId(null);
    }
  };

  const handleAddToQueue = async (patient: Patient) => {
    if (!selectedDoctorId) {
      toast({ title: "Select a doctor first", description: "Please select a doctor to manage queue", variant: "destructive" });
      return;
    }
    try {
      await queueWalkin(
        patient.id,
        selectedDoctorId,
        currentUser!.clinic_id!,
        false
      );
      const updated = await getQueueToday(selectedDoctorId, currentUser!.clinic_id!);
      if (updated) setQueueData(updated);
      setSearchQuery('');
      setSearchResults([]);
      toast({ title: "Added to queue", description: `${patient.name} added to queue` });
    } catch (err: unknown) {
      const msg = err instanceof Error ? err.message : "Failed";
      toast({ title: "Error", description: msg, variant: "destructive" });
    }
  };

  const handleRegisterNew = (query?: string) => {
    setPrefilledPhone(/^\d+$/.test(query ?? '') ? query ?? '' : '');
    setShowPatientFormModal(true);
  };

  const handlePatientFormSuccess = async (newPatient: any, intent: string) => {
    if (intent === 'patient-and-queue' && selectedDoctorId) {
      try {
        await queueWalkin(newPatient.id, selectedDoctorId, currentUser!.clinic_id!, false);
        toast({
          title: "Registered & added to queue",
          description: `${newPatient.name} added successfully`
        });
        await loadData();
        setShowWalkinModal(false);
        setShowPatientFormModal(false);
      } catch (err: unknown) {
        const msg = err instanceof Error ? err.message : "Failed to add to queue";
        toast({ title: "Patient created but queue failed", description: msg, variant: "destructive" });
      }
    } else {
      await loadData();
      setShowPatientFormModal(false);
    }
  };

  const handleOpenInvoice = async (patient: Patient) => {
    setBillingPatient(patient);
    setBillingLoading(true);
    try {
      const services = await getBillingServices(
        currentUser!.clinic_id!,
        currentUser?.doctor_id
      );
      setBillingServices(
        Array.isArray(services) ? services : (services as any)?.services ?? []
      );
    } catch {
      setBillingServices([]);
    } finally {
      setBillingLoading(false);
    }
    setShowBillingModal(true);
    setSearchQuery('');
    setSearchResults([]);
  };

  const handleCreateInvoice = async (invoiceData: any) => {
    if (!billingPatient) return;
    try {
      await createBillingInvoice({
        patient_id: billingPatient.id,
        patient_name: billingPatient.name,
        patient_phone: billingPatient.phone,
        doctor_id: selectedDoctorId ?? currentUser?.doctor_id ?? '',
        clinic_id: currentUser!.clinic_id!,
        items: invoiceData.items,
        discount_amount: invoiceData.discount || 0,
        payment_method: invoiceData.paymentMethod,
        paid_amount: invoiceData.paidAmount ?? invoiceData.finalTotal,
      });
      toast({
        title: "Invoice created",
        description: `Bill created for ${billingPatient.name}`
      });
      setShowBillingModal(false);
      setBillingPatient(null);
    } catch (err: unknown) {
      const msg = err instanceof Error ? err.message : "Failed to create invoice";
      toast({ title: "Error", description: msg, variant: "destructive" });
    }
  };

  const getPatientDetails = (patientId: string) => {
    return patients.find(p => p.id === patientId);
  };

  const isToday = (dateString: string) => {
    const date = new Date(dateString);
    const today = new Date();
    return date.toDateString() === today.toDateString();
  };

  const formatTime = (time: string) => {
    if (!time) return '';
    const [hours, minutes] = time.split(':');
    const h = parseInt(hours);
    const ampm = h >= 12 ? 'PM' : 'AM';
    const hour12 = h % 12 || 12;
    return `${hour12}:${minutes} ${ampm}`;
  };

  // Today's statistics
  const stats = useMemo(() => {
    const todayAppts = appointments.filter(a => isToday(a.date));
    const waiting = queueData?.summary.totalWaiting ?? 0;
    const completed = queueData?.summary.totalCompleted ?? 0;
    return {
      scheduled: todayAppts.filter(a => a.status === 'scheduled').length,
      waiting,
      completed,
      revenue: todayAppts.filter(a => a.status === 'completed').length * 500,
      totalToday: todayAppts.length,
      checkedIn: queueData?.summary.totalInConsultation ?? todayAppts.filter(a => a.status === 'checked_in').length
    };
  }, [appointments, queueData]);

  // Today's appointments with patient data
  const todayAppointments = useMemo(() => {
    return appointments
      .filter(a => isToday(a.date))
      .map(apt => {
        const patient = getPatientDetails(apt.patient_id);
        return {
          ...apt,
          patientName: patient?.name || 'Unknown',
          patientPhone: patient?.phone || '',
          patientMrn: patient?.mrn
        } as AppointmentWithPatient;
      })
      .sort((a, b) => a.time.localeCompare(b.time));
  }, [appointments, patients]);

  // Waiting queue (checked in but not yet seen)
  const waitingQueue = todayAppointments.filter(
    a => a.status === 'checked_in' || a.status === 'scheduled'
  );

  const inConsult = queueData?.entries.find(e => e.status === 'in_consultation');

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary mx-auto mb-4" />
          <p className="text-muted-foreground">Loading dashboard...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="p-6 space-y-5" onClick={() => { if (searchResults.length > 0) setSearchResults([]); }}>
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <div className="p-2 bg-primary rounded-lg">
            <ClipboardList className="w-5 h-5 text-primary-foreground" />
          </div>
          <div>
            <h1 className="text-xl font-bold text-foreground">Front Desk</h1>
            <div className="flex items-center gap-2 mt-0.5">
              <p className="text-sm text-muted-foreground">
                {new Date().toLocaleDateString('en-US', { weekday: 'long', month: 'long', day: 'numeric' })}
              </p>
              <span className="text-sm text-muted-foreground">&bull;</span>
              <span className="text-sm text-muted-foreground">Managing:</span>
              <Select value={selectedDoctorId ?? ''} onValueChange={setSelectedDoctorId}>
                <SelectTrigger className="h-6 text-xs px-2 py-0 border-dashed w-40">
                  <SelectValue placeholder="Select doctor..." />
                </SelectTrigger>
                <SelectContent>
                  {doctors.map(d => (
                    <SelectItem key={d.id} value={d.id}>{d.name.startsWith('Dr.') ? d.name : `Dr. ${d.name}`}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <Button
            onClick={handleRefresh}
            variant="outline"
            disabled={refreshing}
            className="gap-2"
          >
            <RefreshCw className={`w-4 h-4 ${refreshing ? 'animate-spin' : ''}`} />
            Refresh
          </Button>
          <Button
            onClick={() => setShowWalkinModal(true)}
            className="medical-button-primary flex items-center gap-2 px-4 py-2.5"
          >
            <UserPlus className="w-4 h-4" />
            <span>+ Walk-in</span>
          </Button>
        </div>
      </div>

      {/* Search Bar */}
      <div className="relative" onClick={e => e.stopPropagation()}>
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
        <input
          type="text"
          placeholder="Search patient by name or phone..."
          value={searchQuery}
          onChange={e => handleSearch(e.target.value)}
          className="w-full pl-9 pr-4 py-2.5 border border-input rounded-lg bg-background text-sm focus:outline-none focus:border-ring"
        />

        {(searchResults.length > 0 || searchLoading) && searchQuery.length >= 2 && (
          <div className="absolute top-full left-0 right-0 mt-1 bg-popover border border-border rounded-lg shadow-lg z-50">
            {searchLoading ? (
              <div className="flex items-center gap-2 p-3 text-sm text-muted-foreground">
                <Loader2 className="w-4 h-4 animate-spin" />
                Searching...
              </div>
            ) : (
              searchResults.map(patient => (
                <div key={patient.id} className="flex items-center justify-between p-3 hover:bg-accent border-b border-border last:border-0">
                  <div
                    className="flex-1 min-w-0 cursor-pointer"
                    onClick={() => {
                      navigate(`/frontdesk/patients/${patient.id}`);
                      setSearchQuery('');
                      setSearchResults([]);
                    }}
                  >
                    <p className="font-medium text-sm">{patient.name}</p>
                    <p className="text-xs text-muted-foreground">
                      {patient.age}y &middot; {patient.gender} &middot; {patient.phone}
                    </p>
                  </div>
                  <div className="flex gap-2 flex-shrink-0 ml-3">
                    <Button
                      size="sm"
                      variant="ghost"
                      onClick={() => {
                        navigate(`/frontdesk/patients/${patient.id}`);
                        setSearchQuery('');
                        setSearchResults([]);
                      }}
                      className="h-7 w-7 p-0"
                      title="View patient"
                    >
                      <Eye className="w-3.5 h-3.5" />
                    </Button>
                    <Button size="sm" onClick={() => handleAddToQueue(patient)}>
                      + Queue
                    </Button>
                    <Button size="sm" variant="outline" onClick={() => handleOpenInvoice(patient)}>
                      Invoice
                    </Button>
                  </div>
                </div>
              ))
            )}
          </div>
        )}
      </div>

      {/* Stats Bar */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <Card>
          <CardContent className="p-3">
            <p className="text-xs text-muted-foreground">Scheduled</p>
            <p className="text-2xl font-bold text-primary">{stats.scheduled}</p>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-3">
            <p className="text-xs text-muted-foreground">Waiting</p>
            <p className="text-2xl font-bold text-warning">{stats.waiting}</p>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-3">
            <p className="text-xs text-muted-foreground">Completed</p>
            <p className="text-2xl font-bold text-success">{stats.completed}</p>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-3">
            <p className="text-xs text-muted-foreground">Cancelled</p>
            <p className="text-2xl font-bold text-destructive">
              {appointments.filter(a => isToday(a.date) && a.status === 'cancelled').length}
            </p>
          </CardContent>
        </Card>
      </div>

      {/* Main Grid */}
      <div className="grid grid-cols-1 lg:grid-cols-5 gap-6">
        {/* Left Panel — Live Queue */}
        <Card className="lg:col-span-3">
          <CardContent className="p-4">
            <div className="flex items-center justify-between mb-4">
              <h3 className="font-semibold text-sm">Live Queue</h3>
              <span className="text-xs text-muted-foreground">
                {queueData?.summary.totalWaiting ?? 0} waiting
              </span>
            </div>

            {/* In Consultation */}
            {inConsult && (
              <div className="bg-primary/5 ring-2 ring-primary/20 rounded-xl p-3 mb-3">
                <p className="text-xs text-muted-foreground mb-1">IN CONSULTATION</p>
                <div className="flex items-center gap-3">
                  <div className="w-8 h-8 rounded-full bg-warning/20 flex items-center justify-center">
                    <span className="text-sm font-bold text-warning">{inConsult.tokenNumber}</span>
                  </div>
                  <p className="font-semibold">{inConsult.patientName}</p>
                </div>
              </div>
            )}

            {/* Waiting Entries */}
            {queueData?.entries
              .filter(e => e.status === 'waiting')
              .map(entry => (
                <div key={entry.id} className="flex items-center justify-between p-3 rounded-lg bg-muted/20 mb-2">
                  <div className="flex items-center gap-3">
                    <div className="w-8 h-8 rounded-full bg-muted flex items-center justify-center">
                      <span className="text-sm font-bold text-muted-foreground">{entry.tokenNumber}</span>
                    </div>
                    <div>
                      <p className="font-medium text-sm">{entry.patientName}</p>
                      <p className="text-xs text-muted-foreground">
                        {entry.waitingTimeMinutes != null ? `${entry.waitingTimeMinutes} min waiting` : 'Waiting'}
                      </p>
                    </div>
                  </div>
                  <div className="flex items-center gap-1">
                    <Button
                      size="sm" variant="outline"
                      disabled={queueActionId !== null}
                      onClick={() => handlePriority(entry)}
                      title="Move to priority"
                      className="h-7 w-7 p-0"
                    >
                      ↑
                    </Button>
                    <Button
                      size="sm" variant="ghost"
                      disabled={queueActionId !== null}
                      onClick={() => handleRemoveFromQueue(entry)}
                      className="h-7 w-7 p-0 text-destructive hover:text-destructive"
                    >
                      ✕
                    </Button>
                  </div>
                </div>
              ))
            }

            {/* Completed Entries + All Done State */}
            {(() => {
              const completedEntries = queueData?.entries.filter(e => e.status === 'completed') ?? [];
              const hasActive = queueData?.entries.some(e => e.status === 'waiting' || e.status === 'in_consultation') ?? false;
              const allDone = !hasActive && completedEntries.length > 0;

              return (
                <>
                  {completedEntries.length > 0 && (!allDone || completedEntries.length <= 3) &&
                    completedEntries.map(entry => (
                      <div key={entry.id} className="flex items-center gap-3 p-3 rounded-lg opacity-40 mb-1">
                        <span className="text-xs text-success">✓</span>
                        <p className="text-sm text-muted-foreground line-through">{entry.patientName}</p>
                      </div>
                    ))
                  }
                  {allDone && (
                    <div className="flex flex-col items-center justify-center py-8 text-center">
                      <CheckCircle className="w-10 h-10 text-success/40 mb-2" />
                      <p className="text-sm font-medium text-muted-foreground">All done for today</p>
                      <p className="text-xs text-muted-foreground/60 mt-1">No patients waiting</p>
                    </div>
                  )}
                </>
              );
            })()}

            {/* Empty State */}
            {(!queueData || queueData.entries.length === 0) && (
              <div className="text-center py-10 text-muted-foreground">
                <Users className="w-10 h-10 mx-auto mb-2 opacity-30" />
                <p className="text-sm">Queue is empty</p>
                <p className="text-xs mt-1">Patients will appear here after check-in</p>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Right Panel — Today's Appointments */}
        <Card className="lg:col-span-2">
          <CardContent className="p-4">
            <div className="flex items-center justify-between mb-4">
              <h3 className="font-semibold flex items-center gap-2">
                <Calendar className="w-5 h-5 text-primary" />
                Today's Schedule
              </h3>
              <Button
                variant="ghost"
                size="sm"
                onClick={() => navigate('/appointments')}
                className="text-primary"
              >
                View All <ArrowRight className="w-4 h-4 ml-1" />
              </Button>
            </div>

            {todayAppointments.length > 0 ? (
              <div className="space-y-2">
                {todayAppointments.slice(0, 8).map((apt) => (
                  <div
                    key={apt.id}
                    className={`flex items-center justify-between p-3 rounded-lg border ${
                      apt.status === 'completed' ? 'bg-muted/30 opacity-60' :
                      apt.status === 'checked_in' ? 'bg-success/5 border-success/20' :
                      'bg-background'
                    }`}
                  >
                    <div className="flex items-center gap-3 flex-1 min-w-0">
                      <div className="text-center min-w-[50px]">
                        <p className="text-sm font-bold">{formatTime(apt.time)}</p>
                      </div>
                      <div className="min-w-0">
                        <p className="font-medium text-sm truncate">{apt.patientName}</p>
                        {apt.patientPhone && (
                          <p className="text-xs text-muted-foreground flex items-center gap-1">
                            <Phone className="w-3 h-3" />
                            {apt.patientPhone}
                          </p>
                        )}
                      </div>
                    </div>
                    {apt.status === 'scheduled' ? (
                      <Button
                        size="sm"
                        className="bg-success hover:bg-success/90 flex-shrink-0"
                        disabled={checkingInId === apt.id}
                        onClick={() => handleCheckIn(apt)}
                      >
                        {checkingInId === apt.id ? 'Checking in…' : 'Check In'}
                      </Button>
                    ) : apt.status === 'checked_in' ? (
                      <span className="px-2 py-1 bg-success/10 text-success rounded text-xs font-medium flex-shrink-0">
                        ✓ Checked In
                      </span>
                    ) : (
                      <span className={`px-2 py-1 rounded text-xs font-medium flex-shrink-0 ${
                        apt.status === 'completed' ? 'bg-muted text-muted-foreground' :
                        apt.status === 'cancelled' ? 'bg-destructive/10 text-destructive' :
                        'bg-primary/10 text-primary'
                      }`}>
                        {apt.status.charAt(0).toUpperCase() + apt.status.slice(1)}
                      </span>
                    )}
                  </div>
                ))}
              </div>
            ) : (
              <div className="text-center py-8 text-muted-foreground">
                <Calendar className="w-12 h-12 mx-auto mb-2 opacity-50" />
                <p>No appointments today</p>
              </div>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Walk-in Modal */}
      <WalkinModal
        isOpen={showWalkinModal}
        onClose={() => setShowWalkinModal(false)}
        onSuccess={() => {
          setShowWalkinModal(false);
          loadData();
        }}
        doctorId={selectedDoctorId ?? ''}
        clinicId={currentUser?.clinic_id ?? ''}
        tenantId={currentUser?.tenant_id ?? ''}
        patients={patients}
        onRegisterNew={handleRegisterNew}
      />

      <PatientFormModal
        isOpen={showPatientFormModal}
        onClose={() => setShowPatientFormModal(false)}
        onSuccess={handlePatientFormSuccess}
        title="Register New Patient"
        showPrescriptionOption={false}
        showQueueOption={true}
        initialData={prefilledPhone ? { phone: prefilledPhone } : undefined}
      />

      {showBillingModal && billingPatient && (
        <MedicalBillingModal
          isOpen={showBillingModal}
          onClose={() => {
            setShowBillingModal(false);
            setBillingPatient(null);
          }}
          patient={billingPatient}
          services={billingServices}
          onCreateInvoice={handleCreateInvoice}
          loading={billingLoading}
          clinicId={currentUser!.clinic_id!}
          doctorId={selectedDoctorId ?? currentUser?.doctor_id ?? ''}
          onRefreshServices={async () => {
            const s = await getBillingServices(currentUser!.clinic_id!);
            setBillingServices(
              Array.isArray(s) ? s : (s as any)?.services ?? []
            );
          }}
        />
      )}
    </div>
  );
}
